import React, { Dispatch, SetStateAction, useCallback, useContext, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { getTime, startOfDay, endOfDay, subMonths, differenceInDays, subDays, isWithinInterval, format } from 'date-fns'
import makeStyles from '@material-ui/core/styles/makeStyles'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import useT from '../../../i18ns/admin/useT'
import { useGetDataByPayload, useReload } from '../../../utils/default/ComplexFlowHook'
import useGDK from '../../../providers/admin/gdk/useGDK'
import { usePageFlow } from '../../../utils/default/PageFlowHook'
import { AdminJumpShipHistory, AdminPlayerInfo, CountryCallingCode, FeatureFlag, PermissionType, PlayerStatusType } from '@golden/gdk-admin'
import LoadingAndErrorFrame from '../../default/frames/LoadingAndErrorFrame'
import playerStatusName from '../../../constants/default/playerStatusName'
import playerRankName from '../../../constants/default/playerRankName'
import { formatDateTime } from '../../../utils/default/TableHelper'
import allRoute from '../route/route'
import PlayerPhoneInput from './PlayerPhoneInput'
import PlayerEmailInput from './PlayerEmailInput'
import { InfoContext } from '../../../views/admin/player/PlayerDetailPage'
import PlayerNicknameInput from './PlayerNicknameInput'
import PlayerFeatureFlagSwitch from './PlayerFeatureFlagSwitch'
import { forkJoin } from 'rxjs'
import PlayerMemoInput from './PlayerMemoInput'
import { uniq } from '@golden/utils'
import PlayerLocationInput from './PlayerLocationInput'
import PlayerRelationsList from './PlayerRelationsList'
import { useChecker } from '../../../utils/admin/AdminRouteHook'
import JumpShipHistoryTable from './JumpShipHistoryTable'
import ClickAwayListener from '@material-ui/core/ClickAwayListener'
import copy from '../../../assets/admin/images/copy.svg'
import { copyString } from '@golden/shared'

const useStyles = makeStyles((theme) => ({
  lg: {
    [theme.breakpoints.up('lg')]: {
      maxWidth: '20%'
    }
  },
}))

const CopyIcon: React.FC<{
  content?: string,
  style?: React.CSSProperties
}> = ({ content = '', style = {} }) => {
  const handleClick = useCallback(() => {
    copyString(content)
  }, [content])

  return <>
    <Button
      onClick={handleClick}
      style={{
        padding: 0,
        minWidth: 'auto',
        ...style
      }}
    >
      <img src={copy} alt="" style={{ width: '18px', height: '18px' }} />
    </Button>
  </>
}

const PlayerAccountInput: React.FC<{
  account?: string
}> = ({
  account,
}) => (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gridGap: '5px',
      padding: '6px 7px 6px 0px'
    }}>
      <Box>{account}</Box>
      <CopyIcon content={account} />
    </Box>
  )

const StaffInput: React.FC<{
  staff?: string
  newStaffDate?: string | null
  jumpShipHistory?: AdminJumpShipHistory[],
}> = ({ staff, newStaffDate, jumpShipHistory = [] }) => {
  const { t } = useT()
  const commonClasses = useCommonStyles()
  const [isShowSumpShipHistoryTable, setIsShowSumpShipHistoryTable] = useState(false)

  const handleClick = useCallback(() => {
    if (jumpShipHistory.length === 0) return
    setIsShowSumpShipHistoryTable(prev => !prev)
  }, [jumpShipHistory])

  return <div
    style={{
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      padding: '6px 0 7px',
      zIndex: 1,
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gridGap: '5px',
      }}
    >
      <Box>{staff}</Box>
      <CopyIcon content={staff} />
    </Box>
    {jumpShipHistory.length ? <span className={commonClasses.anchor} onClick={handleClick}>{newStaffDate ?? t('common.open2')}</span> : null}
    {isShowSumpShipHistoryTable && (
      <ClickAwayListener onClickAway={() => { setIsShowSumpShipHistoryTable(false) }}>
        <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0 }}>
          <JumpShipHistoryTable jumpShipHistory={jumpShipHistory} />
        </Box>
      </ClickAwayListener>
    )}
  </div>
}

const InviteInfoInput: React.FC<{
  inviteCode?: string
  inviterId?: number
  inviterAccount?: string
  className?: string
  style?: React.CSSProperties
}> = ({ inviteCode, inviterId, inviterAccount, className, style }) => (
  <div
    className={className}
    style={{ ...style, display: 'flex', alignItems: 'center', padding: '6px 0 7px', cursor: 'default', color: 'rgba(0,0,0,0.38)' }}
  >
    {inviteCode ?? '-'}{' / '}
    {inviterId ? (
      <Link
        target="_blank"
        rel="noopener noreferrer"
        to={allRoute.playerDetail.encodePath({ search: {}, param: { id: inviterId } })}
        style={{ marginLeft: 4 }}
      >
        {inviterAccount}
      </Link>
    ) : (
      <span style={{ marginLeft: 4 }}>-</span>
    )}
  </div>
)

interface PropTypes {
  id: number
  section: string
  reloadFlag: boolean
  loaded: Dispatch<SetStateAction<string>>
}

const chipBackground: { [key in PlayerStatusType]: string } = {
  [PlayerStatusType.NORMAL]: '#72d476',
  [PlayerStatusType.NO_CARD]: '#fb9678',
  [PlayerStatusType.SUSPENDED]: '#ff5f5f'
}

const PlayerDetailInfoSection: React.FC<PropTypes> = (props) => {
  const { id, section, reloadFlag: reloadFlagProps, loaded } = props
  const gdk = useGDK()
  const commonClasses = useCommonStyles()
  const { t } = useT()
  const pageFlow = usePageFlow()
  const { reload, reloadFlag } = useReload()
  const startAt = getTime(subMonths(startOfDay(new Date()), 3))
  const endAt = getTime(endOfDay(new Date()))
  const page = 1
  const [res, setRes] = useContext(InfoContext)
  const [featureFlag, setFeatureFlag] = useState<Array<{ slug: FeatureFlag, is_enabled: boolean }>>([])
  const classes = useStyles({ isEmpty: res.jump_ship_history.length === 0 })

  const newStaffDate = useMemo(() => {
    if (res.jump_ship_history.length === 0) return null

    const now = new Date()
    const target = res.jump_ship_history.find(history =>
      history.processed_at && isWithinInterval(history.processed_at, {
        start: subDays(now, 30),
        end: now,
      })
    )

    return target?.processed_at ? format(target.processed_at, 'yyyy-MM-dd') : null
  }, [res])

  const visiblePlayerOperationLogs = useChecker([PermissionType.PLAYER_MANAGEMENT_PLAYER_LOG_READ])
  const visibleLoginIpSearchButton = useChecker([PermissionType.LOGIN_IP_SEARCH_READ])

  useGetDataByPayload({
    payload: id,
    gdkFunc: (payload) => forkJoin([gdk.player.getPlayerInfo(payload), gdk.featureFlag.getPlayerFeatureFlag(payload)]),
    gdkFuncDependencies: [gdk, reloadFlag, reloadFlagProps],
    onBeforeFetch: pageFlow.setLoadingStart,
    onSuccess: (res: [AdminPlayerInfo, Array<{ slug: FeatureFlag, is_enabled: boolean }>]) => {
      setRes(res[0])
      setFeatureFlag(res[1])
      pageFlow.setContentShow()
      loaded(section)
    },
    onError: pageFlow.setGDKError
  })

  return (
    <div id={section}>
      <Paper>
        <Box padding={4}>
          <Box
            paddingY={1.25}
            paddingX={2}
            className={commonClasses.pinkTitleBar}
          >
            <Typography variant="h5">
              {`${t('common.playerInformation')} ( ${uniq((res?.agent_chain ?? [])).concat(res?.account ?? '').join(' > ')} )`}
            </Typography>
          </Box>
          <Box paddingY={2}>
            <LoadingAndErrorFrame {...pageFlow.status}>
              <Grid container direction="row" spacing={2}>
                <Grid item xs={12} md={6} className={classes.lg}>
                  <TextField
                    label={(
                      <React.Fragment>
                        {t('common.playerAccount')}
                        <Box component="span" marginLeft={1} className={commonClasses.chipText} style={{ backgroundColor: chipBackground[res.status] }}>
                          {t(playerStatusName[res.status])}
                        </Box>
                      </React.Fragment>
                    )}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      inputComponent: PlayerAccountInput,
                      inputProps: { account: res.account }
                    }}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6} className={classes.lg}>
                  <PlayerLocationInput id={id} location={res.location} />
                </Grid>
                <Grid item xs={12} md={6} className={classes.lg}>
                  <PlayerNicknameInput id={id} nickname={res.nickname} reload={reload} />
                </Grid>
                <Grid item xs={12} md={6} className={classes.lg}>
                  <TextField
                    label={t('common.playerRank')}
                    value={t(playerRankName[res.rank])}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6} className={classes.lg}>
                  <TextField
                    label={t('common.playerLayer')}
                    value={res.layer_name ?? t('common.none')}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6} className={classes.lg}>
                  <TextField
                    label={t('common.belongStaffName')}
                    disabled
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      inputComponent: StaffInput,
                      inputProps: {
                        staff: res.staff,
                        newStaffDate,
                        jumpShipHistory: res.jump_ship_history
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6} className={classes.lg}>
                  <TextField
                    label={t('common.registeredAt')}
                    value={`${formatDateTime(res.created_at)}${t('common.nthDay', { day: differenceInDays(new Date(), res.created_at) + 1 })}`}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6} className={classes.lg}>
                  <TextField
                    label={t('common.lastLoginAt')}
                    value={res.last_login_at ? formatDateTime(res.last_login_at) : '-'}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid container item xs={12} md={6} className={classes.lg}>
                  <PlayerPhoneInput id={id} iddCode={res.idd_code ?? CountryCallingCode.CN} phone={res.phone} reload={reload} />
                </Grid>
                <Grid item xs={12} md={6} className={classes.lg}>
                  <PlayerEmailInput id={id} email={res.email} reload={reload} disabled={!res.is_allow_modify} />
                </Grid>
                <Grid item xs={12} md={6} className={classes.lg}>
                  <TextField
                    label={t('common.registerIP')}
                    value={res.registered_ip || '-'}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6} className={classes.lg}>
                  <TextField
                    label={t('common.loginIP')}
                    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                    value={res.login_ip || '-'}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6} className={classes.lg}>
                  <TextField
                    label={t('common.santiaoId')}
                    value={res.bubble_id ? res.bubble_id : t('common.noSetting')}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6} className={classes.lg}>
                  <TextField
                    label={t('common.openimId')}
                    value={res.openim_id ? res.openim_id : t('common.noSetting')}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6} className={classes.lg}>
                  <TextField
                    label={t('common.inviteCodeAndInviter')}
                    disabled
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      inputComponent: InviteInfoInput,
                      inputProps: {
                        inviteCode: res.invite_info?.invite_code,
                        inviterId: res.invite_info?.inviter_id,
                        inviterAccount: res.invite_info?.inviter_account
                      }
                    }}
                  />
                </Grid>
                <Grid item container spacing={2}>
                  {(['rc', 'cs', 'fm'] as const).map((type) => (
                    <Grid item xs={12} md={6} lg={4} key={type}>
                      <PlayerMemoInput
                        id={id}
                        res={res}
                        type={type}
                        reload={reload}
                      />
                    </Grid>
                  ))}
                </Grid>
                {
                  featureFlag.map(flag => (
                    <Grid item xs={12} md={6} lg={4}>
                      <PlayerFeatureFlagSwitch id={id} flag={flag} reload={reload} />
                    </Grid>))
                }
              </Grid>
              <Grid item xs={12}>
                <PlayerRelationsList id={id} />
              </Grid>
            </LoadingAndErrorFrame>
          </Box>
          <Grid container justifyContent="flex-end" spacing={2}>
            {visiblePlayerOperationLogs && <Grid item>
              <Button
                component={Link}
                className={commonClasses.purpleGradualButton}
                to={allRoute.playerOperationLogs.encodePath({ param: {}, search: { start_at: getTime(startOfDay(subMonths(new Date(), 3))), end_at: getTime(endOfDay(new Date())), account: res.account, page } })}
              >
                {t('page.playerOperationLogs')}
              </Button>
            </Grid>}
            {visibleLoginIpSearchButton && <Grid item>
              <Button
                component={Link}
                className={commonClasses.purpleGradualButton}
                to={allRoute.playerIp.encodePath({ param: {}, search: { start_at: startAt, end_at: endAt, account: res.account, page } })}
              >
                {t('adminPermission.loginIpSearch')}
              </Button>
            </Grid>}
          </Grid>
        </Box>
      </Paper>
    </div>
  )
}

export default React.memo(PlayerDetailInfoSection)
